<template>
  <v-row class="benefitform match-height">
    <v-col cols="12">
      <v-card>
        <v-card-title>{{ "Henkilöstöedut" }}</v-card-title>
        <v-card-text>
          <v-form v-on:submit.prevent="submit" ref="form">
            <v-text-field outlined hide-details="auto" :label="$t('Yritys')" v-model="benefit.company"
              :rules="[required]"></v-text-field>
            <br />
            <label for="image">Logo</label>
            <br />
            <small class="form-text text-muted">{{
          $t(
            "Raahaa kuva tähän tai klikkaa tästä ja valitse kuva tiedostoista."
          )
        }}</small>
            <br />

            <croppa accept=".jpeg,.jpg,.png" @file-type-mismatch="onFileTypeMismatch" :prevent-white-space="true"
              v-model="logoCroppa" :width="200" :height="200" :initial-image="benefit.logo" @image-remove="removeLogo"
              @new-image-drawn="logoLoaded"></croppa>
            <br />
            <br />
            <label for="coupon">{{ $t("Kuponki") }}</label>
            <br />
            <small class="form-text text-muted">{{
          $t(
            "Raahaa kuva tähän tai klikkaa tästä ja valitse kuva tiedostoista."
          )
        }}</small>
            <br />
            <croppa accept=".jpeg,.jpg,.png" @file-type-mismatch="onFileTypeMismatch" v-model="couponCroppa"
              :width="250" :height="442" :initial-image="benefit.coupon_image" @image-remove="removeCoupon"
              @new-image-drawn="couponLoaded"></croppa>
            <br />
            <br />
            <v-card outlined>
              <v-tabs v-model="tab" grow>
                <v-tab v-for="lang in tenant.languages" :key="'langtab-' + lang.code">
                  <img :src="lang.icon" style="max-height: 20px" />
                  &nbsp;&nbsp;{{ lang.name }}
                  &nbsp;
                  <v-icon v-if="langOk(benefit.translations, lang.code, [
          'title',
          'description',
        ])
          " color="success">
                    {{ icons.mdiCheck }}
                  </v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiBlockHelper }}
                  </v-icon>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="tab">
                <v-tab-item v-for="lang in tenant.languages" :key="'langtabitem-' + lang.code">
                  <div style="padding: 20px">
                    <img :src="lang.icon" style="max-height: 14px" />
                    <br />
                    <br />
                    <v-text-field outlined hide-details="auto" v-model="benefit.translations[lang.code].title"
                      :label="$t('Otsikko')" class="mb-4" />
                    <div style="height: 200px">
                      <v-textarea outlined hide-details="auto" :label="$t('Kuvaus')"
                        v-model="benefit.translations[lang.code].description" height="200" />
                    </div>
                  </div>
                </v-tab-item>
              </v-tabs-items>
              <v-input class="ml-4 mb-4" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
            </v-card>

            <v-switch v-model="benefit.published" inset persistent-hint :hint="benefit.published
          ? $t('Etu julkaistaan heti')
          : $t('Etu tallennetaan luonnoksena')
          " :label="$t('Julkaise')"></v-switch>
            <br />
            <v-alert color="error" text v-if="!formIsValid">
              {{ $t("Tarkista lomakkeen tiedot") }}
            </v-alert>
            <v-btn color="error" v-if="benefit.id" @click="removeBenefit" class="mr-3">{{ $t("Poista") }}</v-btn>
            <v-btn color="primary" @click="submit">{{ $t("Tallenna") }}</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, reactive, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import { useRouter } from "@/@core/utils";
import useAuth from "@/useAuth";
import {
  convertTranslationsHtmlToMarkdown,
  convertTranslationMarkdownToHtml,
  getValidTranslations,
  langOk,
} from "@/@core/utils/translation_helpers";
import {
  mdiCheck,
  mdiBlockHelper,
  mdiClockOutline,
  mdiCalendar,
  mdiAttachment,
  mdiDeleteOutline,
} from "@mdi/js";
import "vue-croppa/dist/vue-croppa.css";
import { required } from "@/@core/utils/validation";
import { i18n } from "@/plugins/i18n";

export default {
  props: ["id", "parent"],
  setup(props) {
    const { router, route } = useRouter();
    const { tenant, user } = useAuth();
    const formIsValid = ref(true);
    const tab = ref(null);
    const form = ref(null);
    const editorOption = {
      modules: {
        clipboard: {
          keepSelection: true,
          substituteBlockElements: false,
          magicPasteLinks: true,
        },
        toolbar: [
          ["bold", "italic"],
          ["link"],
        ],
      },
    };
    const logoCroppa = ref({});
    const couponCroppa = ref({});
    const logoCroppaInitialMedadata = {
      startX: 0,
      startY: 0,
      scale: 1,
      orientation: 1,
    };

    const benefit = reactive({
      id: null,
      company: "",
      benefits: "",
      translations: {},
      published: true,
      logo: null,
      coupon_image: null,
    });
    const logoRemoved = ref(false);
    const couponRemoved = ref(false);

    tenant.value.languages.forEach((lang) => {
      benefit.translations[lang.code] = {
        title: "",
        description: "",
      };
    });

    const fetchBenefit = async (id) => {
      const response = await adminApi.getBenefit(id);
      if (response.ok) {
        benefit.id = response.data.id;
        benefit.published = response.data.published;
        benefit.company = response.data.company;

        // Translations
        benefit.translations = {};
        tenant.value.languages.forEach((lang) => {
          if (response.data.translations[lang.code]) {
            benefit.translations[lang.code] =
              response.data.translations[lang.code];
          } else {
            benefit.translations[lang.code] = {
              title: "",
              description: "",
            };
          }
        });

        // Coupon
        if (
          response.data.coupon_image &&
          Object.keys(response.data.coupon_image)
        ) {
          const fullSize = response.data.coupon_image["full_size"];
          if (fullSize) {
            benefit.coupon_image = fullSize;
          }
        }

        // Logo
        if (response.data.logo && Object.keys(response.data.logo)) {
          const fullSize = response.data.logo["full_size"];
          if (fullSize) {
            benefit.logo = fullSize;
          }
        }

        // croppa
        if (benefit.coupon_image) {
          couponCroppa.value.refresh();
        }
        // croppa
        if (benefit.logo) {
          logoCroppa.value.refresh();
        }
      } else {
        alert(response.error);
      }
    };

    const submit = async () => {
      formIsValid.value = true;
      if (form.value.validate()) {
        const payload = Object.assign({}, benefit);

        // Translations
        payload.translations = getValidTranslations(benefit.translations, [
          "title",
          "description",
        ]);

        // Logo
        let newLogoName = "";
        let logoBlob = null;
        let removeLogo = false;
        if (logoCroppa.value.hasImage()) {
          if (logoCroppa.value.chosenFile) {
            logoBlob = await logoCroppa.value.promisedBlob(
              logoCroppa.value.chosenFile.type,
              0.8
            );
            newLogoName = logoCroppa.value.chosenFile.name;
          }
        } else if (logoRemoved.value) {
          removeLogo = true;
        }

        // Coupon
        let newCouponName = "";
        let couponBlob = null;
        let removeCouponImage = false;
        if (couponCroppa.value.hasImage()) {
          if (couponCroppa.value.chosenFile) {
            couponBlob = await couponCroppa.value.promisedBlob(
              couponCroppa.value.chosenFile.type,
              0.8
            );
            newCouponName = couponCroppa.value.chosenFile.name;
          }
        } else if (couponRemoved.value) {
          removeCouponImage = true;
        }

        const response = await adminApi.saveBenefit(
          payload,
          logoBlob,
          newLogoName,
          removeLogo,
          couponBlob,
          newCouponName,
          removeCouponImage
        );

        if (response.ok) {
          router.push({ name: "admin-benefit-list" });
        } else {
          alert(response.error);
        }
      } else {
        formIsValid.value = false;
      }
    };

    const removeBenefit = async () => {
      if (confirm(i18n.t("Haluatko varmasti poistaaa edun?"))) {
        const response = await adminApi.deleteBenefit(benefit.id);
        if (response.ok) {
          router.replace({ name: "admin-benefit-list" });
        } else {
          alert(response.error);
        }
      }
    };

    const logoLoaded = () => {
      logoRemoved.value = false;
    };

    const couponLoaded = () => {
      couponImageRemoved.value = false;
    };

    const removeLogo = () => {
      benefit.logo = null;
      logoRemoved.value = true;
    };

    const removeCoupon = () => {
      benefit.coupon_image = null;
      couponRemoved.value = true;
    };

    const onFileTypeMismatch = (file) => {
      alert(
        i18n.t(
          "Tiedostomuoto ei ole tuettu. Tuettuja tiedostomuotoja ovat jpeg sekä png."
        )
      );
    };

    const validateLangs = () => {
      let valid = false;
      tenant.value.languages.forEach((lang) => {
        if (langOk(benefit.translations, lang.code, ["title", "description"]))
          valid = true;
        else {

        }
      });

      if (valid) {
        return true;
      } else {
        return i18n.t("Otsikko tai kuvaus puuttuu");
      }
    };

    onMounted(() => {
      if (props.id) {
        fetchBenefit(props.id);
      }
    });

    return {
      benefit,
      tenant,
      formIsValid,
      tab,
      form,
      logoLoaded,
      couponLoaded,
      removeLogo,
      removeCoupon,
      editorOption,
      logoCroppa,
      couponCroppa,
      logoCroppaInitialMedadata,
      onFileTypeMismatch,
      langOk,
      submit,
      removeBenefit,
      required,
      validateLangs,
      icons: {
        mdiCheck,
        mdiBlockHelper,
        mdiClockOutline,
        mdiCalendar,
        mdiAttachment,
        mdiDeleteOutline,
      },
    };
  },
};
</script>

<style></style>